import React, { useState, useEffect, Fragment } from 'react'
import { Breadcrumb } from 'react-breadcrumbs'
import injectStyles from 'react-jss'
import { Link, useRouteMatch } from 'react-router-dom'
import moment from 'moment'

import EditIcon from '@material-ui/icons/Edit'
import Checkbox from '@material-ui/core/Checkbox'
import { MasterScheduleWrapper } from 'Components/City/Page/MasterSchedule'
import CityPage from '../../../../Containers/City/Page'
import StyledIconButton from '../../../../Components/Form/StyledIconButton'
import { StatusLabel } from '../../../../Components/StatusLabel'
import { StarRating } from '../../../../Components/StarRating'
import LoadingIndicator from '../../../../Components/LoadingIndicator/LoadingIndicator'
import EnhancedTable from './EnhancedTable'
import DeleteIcon from 'Assets/Images/icons/delete'
import AssignSpyToolbar from './AssignSpyToolbar'
import SearchAndFilters from './Filters'

import useGraphQLFetch from '../../../../Utils/Hooks/useGraphQLFetch'
import {
  defaultMissionFilters,
  getOptions,
  getWeeksFilterOptions,
} from '../../../../Utils/Table'
import { formatStringToFirstUpperCase } from '../../../../Utils/Formatters'
import { getCurrentTimeOfDay } from '../../../../Utils/Date'
import { urlGet, urlRemove } from '../../../../Utils/URL'
import {
  formatRows,
  setUrl,
  switchOrderBy,
  getUpdatedQueryAndLabels,
  getUpdatedMappedFilters,
  onClickRow,
  getTitle,
} from './utils'
import {
  getMissionsByCity,
  updateMission,
  bookSpyMissions,
  deleteSpyShift,
} from './gql'
import { MISSION_STATUS_ENUM } from '../../../../shared/constants'
import {
  getLongWeekDay,
  getWeekDayIndex,
} from '../../../../shared/libraries/utils/date'
import { defaultModalState, headers, MODAL_TYPES, presets } from './options'
import { styles } from './styles'
import { ModalConfirmation } from '../../../../Components/Modal/ModalConfirmation'

const MasterSchedule = props => {
  const {
    classes = {},
    clients,
    locations,
    campaigns,
    cityId,
    afternoonCheckInTime,
    lunchCheckInTime,
    timezone,
    spySettings,
    spies,
  } = props

  const [rowsData, setRowsData] = useState([])
  const [order, setOrder] = useState('ASC')
  const [orderBy, setOrderBy] = useState(null)
  const [resultPerPage, setResultPerPage] = useState(50)
  const [skip, setSkip] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(0)
  const [searchText, setSearchText] = useState('')
  const [mappedFilters, setMappedFilters] = useState([])
  const [suggestions, setSuggestions] = useState([])
  const [activeTags, setActiveTags] = useState([])
  const [presetValue, setPresetValue] = useState('No Preset')
  const [selectedMissions, setSelectedMissions] = useState([])
  const [disabledCheckbox, setDisabledCheckbox] = useState(false)
  const [changedCheckBox, setChangedCheckBox] = useState({})
  const [customRefetch, setCustomRefetch] = useState(false)
  const [currentData, setCurrentData] = useState([])
  const [modalInformation, setModalInformation] = useState(defaultModalState)
  const [title, setTitle] = useState(
    getTitle({
      numSelected: selectedMissions.length,
      neededCount: spySettings ? spySettings.missionsPerShift : null,
    }),
  )
  const [selectedFilters, setSelectedFilters] = useState({
    query: {},
    labels: {},
  })

  const match = useRouteMatch()

  const currentTimeOfDay = getCurrentTimeOfDay({
    afternoonCheckInTime,
    lunchCheckInTime,
    timezone,
  })

  const { data, isLoaded, refetch } = useGraphQLFetch({
    fetchFn: getMissionsByCity,
    defaultFetchArgs: {
      cityId,
      first: resultPerPage,
      skip,
      orderBy,
      filter: searchText,
      ...selectedFilters.query,
    },
    mapResponse: data => formatRows(data),
    skipFetchOnInit: true,
  })

  useEffect(() => {
    const filters = [
      {
        label: 'Client',
        options: getOptions(clients),
      },
      {
        label: 'Location',
        options: getOptions(locations),
      },
      {
        label: 'Campaign',
        options: getOptions(campaigns),
      },
      {
        label: 'Week',
        options: getWeeksFilterOptions(),
      },
      ...defaultMissionFilters,
      {
        label: 'Reported',
        options: [
          { label: 'Only Reported', value: true },
          { label: 'Only Not Reported', value: false },
        ],
      },
    ]
    if (locations.length || clients.length || campaigns.length) {
      const search = urlGet('search')
      if (search) {
        setSearchText(search)
        setTagsAndSuggestions({ tag: search, option: 'add' })
      }
      const newFilters = filters.map(({ label, options }, index) => {
        let selectedValue = 'All'
        const labelValue = urlGet(label)
        const labels = options.map(({ label }) => label)
        setSuggestions(prev => [...prev, ...labels])
        if (labelValue) {
          const findOption = options.find(item => item.label === labelValue)
          const queryValue = findOption ? findOption.value : null
          setTagsAndSuggestions({ tag: labelValue, option: 'add' })
          setSelectedFilters(prev =>
            getUpdatedQueryAndLabels(prev, [{ label, labelValue, queryValue }]),
          )
          selectedValue = queryValue
        }
        return {
          data: {
            label,
            options,
            selectedValue,
          },
          config: {
            type: 'select',
            filterIndex: index,
          },
        }
      })
      setMappedFilters(newFilters)
    }
  }, [])

  useEffect(() => {
    if (isLoaded && data) {
      setCurrentData(data.nodes)
      setTotalCount(data.totalCount)
    }
  }, [isLoaded])

  useEffect(() => {
    if (isLoaded && data) {
      setTitle(
        getTitle({
          numSelected: selectedMissions.length,
          neededCount: spySettings ? spySettings.missionsPerShift : null,
        }),
      )
      if (
        selectedMissions.length >=
        (spySettings ? spySettings.missionsPerShift : 0)
      ) {
        setDisabledCheckbox(true)
      } else {
        setDisabledCheckbox(false)
      }
    }

    if (currentData.length) {
      setRowsData(createRows(currentData))
    }
  }, [selectedMissions.length, disabledCheckbox, isLoaded])

  useEffect(() => {
    setRowsData(createRows(currentData))
  }, [currentData.length])

  useEffect(() => {
    if (currentData.length && changedCheckBox.id) {
      setCurrentData(prev => {
        return prev.reduce((acc, item) => {
          if (item.id === changedCheckBox.id) {
            acc.push({
              ...item,
              canBeSpied: changedCheckBox.canBeSpied,
              spy: changedCheckBox.canBeSpied ? item.spy : false,
            })
          } else {
            acc.push({ ...item })
          }
          return acc
        }, [])
      })

      setCustomRefetch(prev => !prev)
      setRowsData(createRows(currentData))
    }
  }, [changedCheckBox.id, changedCheckBox.canBeSpied])

  useEffect(() => {
    if (currentData.length && changedCheckBox.id) {
      setRowsData(createRows(currentData))
    }
  }, [customRefetch])

  useEffect(() => {
    refetchAndDropSelectedMissions({
      cityId,
      first: resultPerPage,
      skip,
      orderBy,
      filter: searchText,
      ...selectedFilters.query,
    })
  }, [
    orderBy,
    resultPerPage,
    skip,
    ...Object.values(selectedFilters.query),
    searchText,
    presetValue,
  ])

  const refetchAndDropSelectedMissions = refetchValues => {
    setSelectedMissions([])
    setCurrentData([])
    refetch(refetchValues)
  }

  const onChangePage = page => {
    setSkip(resultPerPage * page)
    setPage(page)
  }

  const onChangeRowsPerPage = value => {
    setResultPerPage(value)
    setPage(0)
  }

  const onChange = ({ value, label, data }) => {
    setPresetValue('No Preset')
    const findOption = data.options.find(item => item.value === value)
    const labelValue = findOption ? findOption.label : null
    setMappedFilters(prev =>
      getUpdatedMappedFilters({
        prev,
        updatedValues: [
          {
            label,
            queryValue: value,
          },
        ],
        isOnlyOne: true,
      }),
    )
    setUrl(label, labelValue)
    setSelectedFilters(prev =>
      getUpdatedQueryAndLabels(prev, [
        { label, labelValue, queryValue: value },
      ]),
    )

    if (labelValue) {
      setTagsAndSuggestions({ tag: labelValue, option: 'add' })
    }
    if (activeTags.includes(selectedFilters.labels[label])) {
      setTagsAndSuggestions({
        tag: selectedFilters.labels[label],
        option: 'remove',
      })
    }
    setSkip(0)
  }

  const onAddTag = tag => {
    setPresetValue('No Preset')
    const data = mappedFilters.reduce((acc, item) => {
      const option = item.data.options.find(option => option.label === tag)
      if (option) {
        acc = { ...item.data, option }
      }
      return acc
    }, null)
    if (data) {
      const existingTag = selectedFilters.labels[data.label]
      if (existingTag) {
        setTagsAndSuggestions({ tag: existingTag, option: 'remove' })
      }
      setSelectedFilters(prev =>
        getUpdatedQueryAndLabels(prev, [
          {
            label: data.label,
            labelValue: data.option.label,
            queryValue: data.option.value,
          },
        ]),
      )

      setMappedFilters(prev =>
        getUpdatedMappedFilters({
          prev,
          updatedValues: [
            {
              label: data.label,
              queryValue: data.option.value,
            },
          ],
          isOnlyOne: true,
        }),
      )

      setUrl(data.label, tag)
    }
    setTagsAndSuggestions({ tag, option: 'add' })
  }

  const onRemoveTag = tag => {
    setPresetValue('No Preset')
    const data = Object.entries(selectedFilters.labels).find(
      ([label, value]) => value === tag,
    )
    const label = data ? data[0] : null
    if (label) {
      urlRemove(label)
      setSelectedFilters(prev =>
        getUpdatedQueryAndLabels(prev, [
          {
            label,
            labelValue: null,
            queryValue: null,
          },
        ]),
      )
      setMappedFilters(prev =>
        getUpdatedMappedFilters({
          prev,
          updatedValues: [
            {
              label,
              queryValue: 'All',
            },
          ],
          isOnlyOne: true,
        }),
      )
    } else {
      setSearchText(null)
      setUrl('search')
    }
    setTagsAndSuggestions({ tag, option: 'remove' })
  }
  const createRows = data =>
    data.map(row => {
      const warning = `Are you sure you want to delete ${
        row.spy
      } from this mission?
        
        "ATTENTION: Removing spy from this mission will also affect all other missions in this shift"`
      return {
        params: {
          highLight: row.isReported,
          id: row.id,
          disableClick: ['canBeSpied', 'spy'],
        },
        row: {
          clientName: row.clientName,
          campaignName: row.campaignName,
          week: row.week,
          date: row.date,
          time: row.time,
          timeOfDay: row.timeOfDay,
          dayOfWeek: row.dayOfWeek,
          canBeSpied:
            row.status === MISSION_STATUS_ENUM.BOOKED ? (
              <Checkbox
                disabled={!spySettings || row.isSpied}
                checked={row.canBeSpied}
                onChange={async (event, canBeSpied) => {
                  if (!row.shiftId) {
                    const response = await updateMission({
                      id: row.id,
                      canBeSpied,
                    })
                    const { data } = await response.json()
                    const mission = data.updateMission.mission

                    if (mission.id) {
                      setChangedCheckBox(mission)

                      // update selected missions state if we set mission
                      // to canBeSpied = false
                      !canBeSpied &&
                        setSelectedMissions(prev => [
                          ...prev.filter(item => item !== mission.id),
                        ])
                    }
                  } else {
                    setModalInformation({
                      text: warning,
                      shiftId: row.shiftId,
                      isOpen: true,
                      type: MODAL_TYPES.UPDATE_MISSION,
                      missionId: row.id,
                      canBeSpied,
                    })
                  }
                }}
              />
            ) : (
              ''
            ),
          spy: row.spy ? (
            <div className={classes.spyColumn}>
              <div>{row.spy}</div>
              <div className={classes.deleteIconWrapper}>
                {row.status === MISSION_STATUS_ENUM.BOOKED && !row.isSpied ? (
                  <DeleteIcon
                    onClick={() => {
                      setModalInformation({
                        text: warning,
                        shiftId: row.shiftId,
                        isOpen: true,
                        type: MODAL_TYPES.DELETE_SPY,
                      })
                    }}
                    width={18}
                    height={18}
                    fill="#8A96A0"
                  />
                ) : null}
              </div>
            </div>
          ) : row.canBeSpied && row.status === MISSION_STATUS_ENUM.BOOKED ? (
            <Checkbox
              onChange={async (e, value) => {
                if (value) {
                  setSelectedMissions(prev => [...prev, row.id])
                } else {
                  setSelectedMissions(prev => [
                    ...prev.filter(item => item !== row.id),
                  ])
                }
              }}
              disabled={disabledCheckbox && !selectedMissions.includes(row.id)}
            />
          ) : (
            ' - '
          ),
          location: row.location,
          brandAmbassador: row.brandAmbassador,
          flyerCode: row.flyerCode,
          tag: row.tag,
          flyersDistributed: row.flyersDistributed ? row.flyersDistributed : 0,
          scans: row.scans,
          metric1: row.metric1,
          metric2: row.metric2,
          status: <StatusLabel isSpied={row.isSpied} status={row.status} />,
          rating:
            row.status in
            {
              'UNDER_REVIEW': true,
              'COMPLETED': true,
              'PAID': true,
            } ? (
              <div
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
              >
                <StarRating
                  uniqueId={'rate-' + row.id}
                  id={'rate-' + row.id}
                  score={parseFloat(row.rating, 10) || 0}
                />
              </div>
            ) : (
              ' - '
            ),
        },
      }
    })

  const handleRequestSort = (event, property) => {
    const sort = !orderBy ? 'ASC' : order === 'ASC' ? 'DESC' : 'ASC'
    const column = switchOrderBy(property)
    setOrderBy(column)
    const value = `${column.toUpperCase()}_${sort}`
    setOrder(sort)
    setOrderBy(value)
  }

  const onPressInput = searchValue => {
    setUrl('search', searchValue)
    setSearchText(searchValue)
    setPresetValue('No Preset')

    refetchAndDropSelectedMissions({
      cityId,
      first: resultPerPage,
      skip,
      orderBy,
      filter: searchValue,
      ...selectedFilters.query,
    })
  }

  const setTagsAndSuggestions = ({ tag, option }) => {
    if (option === 'add') {
      setActiveTags(prev => [...prev, tag])
      setSuggestions(prev => [...prev.filter(item => item !== tag)])
    }
    if (option === 'remove') {
      setActiveTags(activeTags => activeTags.filter(item => item !== tag))
      setSuggestions(prev => [...prev, tag])
    }
  }

  const onChangePreset = ({ id, label }) => {
    setPresetValue(label)
    const keys = Object.keys(selectedFilters.labels)
    if (keys.length) {
      keys.forEach(key => {
        setUrl(key)

        const tag = selectedFilters.labels[key]
        if (tag) {
          setTagsAndSuggestions({
            tag,
            option: 'remove',
          })
        }

        setSelectedFilters(prev =>
          getUpdatedQueryAndLabels(prev, [
            {
              label: key,
              labelValue: null,
              queryValue: null,
            },
          ]),
        )
      })
    }

    if (id === 'noPresets') {
      setMappedFilters(prev => getUpdatedMappedFilters({ prev }))
    } else {
      const dateTime = new Date()
      let dateWeekNumber = moment(dateTime).week()
      const dateYearNumber = moment(dateTime).year()
      const weekQueryValue = {
        weekNumber: dateWeekNumber,
        year: dateYearNumber,
      }

      if (id === 'liveMissions') {
        const today = getLongWeekDay(getWeekDayIndex(dateTime))
        const timeOfDay = formatStringToFirstUpperCase(currentTimeOfDay)

        const updatePresetValues = [
          {
            label: 'Week',
            labelValue: `W${dateWeekNumber}`,
            queryValue: weekQueryValue,
          },
          {
            label: 'Time of day',
            labelValue: timeOfDay,
            queryValue: timeOfDay.toUpperCase(),
          },
          {
            label: 'Day of week',
            labelValue: today,
            queryValue: today.toUpperCase(),
          },
        ]

        updatePresetValues.forEach(({ label, labelValue }) => {
          setUrl(label, labelValue)
          setTagsAndSuggestions({ tag: labelValue, option: 'add' })
        })
        setSelectedFilters(prev =>
          getUpdatedQueryAndLabels(prev, updatePresetValues),
        )

        setMappedFilters(prev =>
          getUpdatedMappedFilters({
            prev,
            updatedValues: updatePresetValues,
            date: {
              dateWeekNumber,
              dateYearNumber,
            },
          }),
        )
      }
      if (id === 'yesterdayMissions') {
        const yesterday = getLongWeekDay(
          getWeekDayIndex(dateTime) == 0 ? 6 : getWeekDayIndex(dateTime) - 1,
        )

        if (yesterday === 'Sunday') {
          dateWeekNumber -= 1
        }

        const updatePresetValues = [
          {
            label: 'Week',
            labelValue: `W${dateWeekNumber}`,
            queryValue: weekQueryValue,
          },
          {
            label: 'Day of week',
            labelValue: yesterday,
            queryValue: yesterday.toUpperCase(),
          },
        ]

        updatePresetValues.forEach(({ label, labelValue }) => {
          setUrl(label, labelValue)
          setTagsAndSuggestions({ tag: labelValue, option: 'add' })
        })

        setSelectedFilters(prev =>
          getUpdatedQueryAndLabels(prev, updatePresetValues),
        )

        setMappedFilters(prev =>
          getUpdatedMappedFilters({
            prev,
            updatedValues: updatePresetValues,
            date: {
              dateWeekNumber,
              dateYearNumber,
            },
          }),
        )
      }
      if (id === 'todayMissions') {
        const today = getLongWeekDay(getWeekDayIndex(dateTime))

        const updatePresetValues = [
          {
            label: 'Week',
            labelValue: `W${dateWeekNumber}`,
            queryValue: weekQueryValue,
          },
          {
            label: 'Day of week',
            labelValue: today,
            queryValue: today.toUpperCase(),
          },
        ]

        updatePresetValues.forEach(({ label, labelValue }) => {
          setUrl(label, labelValue)
          setTagsAndSuggestions({ tag: labelValue, option: 'add' })
        })

        setSelectedFilters(prev =>
          getUpdatedQueryAndLabels(prev, updatePresetValues),
        )

        setMappedFilters(prev =>
          getUpdatedMappedFilters({
            prev,
            updatedValues: updatePresetValues,
            date: {
              dateWeekNumber,
              dateYearNumber,
            },
          }),
        )
      }
      if (id === 'tomorrowMissions') {
        const tomorrow = getLongWeekDay(getWeekDayIndex(dateTime) + 1)
        if (tomorrow === 'Monday') {
          dateWeekNumber += 1
        }
        const updatePresetValues = [
          {
            label: 'Week',
            labelValue: `W${dateWeekNumber}`,
            queryValue: weekQueryValue,
          },
          {
            label: 'Day of week',
            labelValue: tomorrow,
            queryValue: tomorrow.toUpperCase(),
          },
        ]

        updatePresetValues.forEach(({ label, labelValue }) => {
          setUrl(label, labelValue)
          setTagsAndSuggestions({ tag: labelValue, option: 'add' })
        })

        setSelectedFilters(prev =>
          getUpdatedQueryAndLabels(prev, updatePresetValues),
        )

        setMappedFilters(prev =>
          getUpdatedMappedFilters({
            prev,
            updatedValues: updatePresetValues,
            date: {
              dateWeekNumber,
              dateYearNumber,
            },
          }),
        )
      }
    }
  }

  const assignSpyToMissions = async spyId => {
    await bookSpyMissions({
      userId: spyId,
      shiftMissions: selectedMissions.map(item => ({ missionId: item })),
    })

    refetchAndDropSelectedMissions({
      cityId,
      first: resultPerPage,
      skip,
      orderBy,
      filter: searchText,
      ...selectedFilters.query,
    })
  }

  const handleConfirmModal = async () => {
    if (modalInformation.type === MODAL_TYPES.DELETE_SPY) {
      await deleteSpyShift({
        id: modalInformation.shiftId,
      })

      setModalInformation(defaultModalState)

      refetchAndDropSelectedMissions({
        cityId,
        first: resultPerPage,
        skip,
        orderBy,
        filter: searchText,
        ...selectedFilters.query,
      })
    }

    if (modalInformation.type === MODAL_TYPES.UPDATE_MISSION) {
      const response = await updateMission({
        id: modalInformation.missionId,
        canBeSpied: modalInformation.canBeSpied,
      })
      const { data } = await response.json()
      const mission = data.updateMission.mission

      if (mission.id) {
        setChangedCheckBox(mission)
      }

      if (!modalInformation.canBeSpied && modalInformation.shiftId) {
        await deleteSpyShift({
          id: modalInformation.shiftId,
        })

        refetchAndDropSelectedMissions({
          cityId,
          first: resultPerPage,
          skip,
          orderBy,
          filter: searchText,
          ...selectedFilters.query,
        })
      }
      setModalInformation(defaultModalState)
    }
  }

  return (
    <CityPage
      active={props.nav}
      cityId={props.cityId}
      history={props.history}
      match={props.match}
      cityName={props.cityName}
      className={classes.wrapper}
    >
      <Breadcrumb
        data={{ title: 'Global Planning', pathname: props.match.url }}
      />
      <ModalConfirmation
        key="modal-confirmation"
        modalShow={modalInformation.isOpen}
        handleCancel={() => setModalInformation(defaultModalState)}
        handleConfirm={handleConfirmModal}
        title="Confirmation needed"
        text={modalInformation.text}
        labelCancel="Cancel"
        labelConfirm="Confirm"
        closeButton={false}
      />
      <MasterScheduleWrapper>
        <Link
          to={match.url.replace('global-planning', 'edit')}
          className="link-edit-main"
        >
          <StyledIconButton>
            <EditIcon />
          </StyledIconButton>
        </Link>
        {mappedFilters.length ? (
          <Fragment>
            <SearchAndFilters
              onChange={onChange}
              enableFilterSuggestions={true}
              filters={mappedFilters}
              searchText={searchText}
              className={classes.filtersContainer}
              suggestions={suggestions}
              activeTags={activeTags}
              setActiveTags={setActiveTags}
              setSuggestions={setSuggestions}
              onAddTag={onAddTag}
              onRemoveTag={onRemoveTag}
              onPressInput={onPressInput}
              onChangePreset={onChangePreset}
              presets={presets}
              presetValue={presetValue}
            />
            {isLoaded ? (
              <div className={classes.wrapperTable}>
                <EnhancedTable
                  headers={headers}
                  rows={rowsData}
                  handleRequestSort={handleRequestSort}
                  order={order}
                  orderBy={orderBy}
                  resultPerPage={resultPerPage}
                  onChangePage={onChangePage}
                  totalCount={totalCount}
                  onChangeRowsPerPage={onChangeRowsPerPage}
                  page={page}
                  overHeaderComponent={
                    <AssignSpyToolbar
                      numSelected={selectedMissions.length}
                      neededCount={
                        spySettings ? spySettings.missionsPerShift : 0
                      }
                      title={title}
                      spies={spies}
                      onConfirm={assignSpyToMissions}
                    />
                  }
                  onClickRow={onClickRow}
                />
              </div>
            ) : (
              <div className={classes.loadingTableWrapper}>
                <LoadingIndicator />
              </div>
            )}
          </Fragment>
        ) : (
          <div className={classes.loadingAllWrapper}>
            <LoadingIndicator />
          </div>
        )}
      </MasterScheduleWrapper>
    </CityPage>
  )
}

export default injectStyles(styles)(MasterSchedule)



// WEBPACK FOOTER //
// ./src/Containers/City/Page/MasterSchedule/index.js